/**
 * Phase 4b PR 3 — authFetch (pure function form).
 *
 * Mirrors the vanilla helper at src/renderer/js/auth-fetch.js:
 *   1. Get current access token from Supabase.
 *   2. If no token, attempt silent refresh.
 *   3. Send request with `Authorization: Bearer <token>`.
 *   4. On 401, attempt one refresh + retry.
 *
 * Pure function — takes the SupabaseClient as a parameter so the
 * function is unit-testable without React context. The hook form
 * (`useAuthFetch`) is the convenience wrapper components use.
 *
 * Why duplicate the vanilla pattern instead of importing from
 * src/renderer/: vanilla is a different module system (raw ES modules
 * served from dist) and `state.supabaseClient` is a global. The TS
 * port takes the client explicitly — cleaner contract, plus the
 * vanilla module can deprecate at Phase 7 cutover without React
 * needing a coordinated change.
 */

import type { SupabaseClient } from '@supabase/supabase-js';
import { apiUrl } from './apiBase';

/**
 * Fetch a URL with auth headers + automatic 401 refresh-and-retry.
 *
 * @param supabase - SupabaseClient from AuthContext (or null pre-init)
 * @param url      - URL or path; relative paths resolve against the page origin
 * @param init     - Standard RequestInit, plus an optional `surface` field
 *                   that maps to the `X-Surface` header (Phase 4a telemetry,
 *                   see api/lib/surface.js — telemetry-only, never drives logic).
 *
 * Returns the Response object, never throws on HTTP errors (caller
 * inspects `response.ok` / `response.status`). Throws only on network
 * errors or if `supabase` is null AND a retry is needed.
 */
export interface AuthFetchInit extends RequestInit {
  /** Sets the X-Surface telemetry header. 'react' from React-side callers. */
  surface?: 'vanilla' | 'react';
  /**
   * B234 (2026-09-26) — the uid a LONG-RUNNING action recorded when it
   * started (e.g. `EditPanel.onApply`, the HealthKit import POST loop).
   * `authFetch` refuses to send (no network call) if the CURRENT session's
   * uid no longer matches: an in-place account switch mid-action must never
   * let the rest of that action's requests land under the new account's
   * token. `undefined` (the default — every pre-existing caller) means "no
   * guard", byte-identical to before. `null` matches "signed out".
   */
  expectedUid?: string | null;
}

// ─── Refresh stampede guard (2026-08-29 incident) ────────────────────
// ONE refreshSession() in flight per client, its result shared for a
// short window. Without this, a cold boot with an expired token fired
// one refresh PER concurrent authFetch call (~20 at boot, more under
// retry); Supabase rotates the refresh token on every use and
// rate-limits /token, so the stampede produced 429s and stale-token
// reuse — either of which makes supabase-js drop the session and sign
// the user out. A failed shared refresh resolves null and never touches
// the session: callers proceed unauthenticated (or return their 401)
// and the next window may try again.
const REFRESH_SHARE_MS = 5000;
const refreshShares = new WeakMap<
  SupabaseClient,
  { at: number; p: Promise<string | null> }
>();

function sharedRefreshToken(supabase: SupabaseClient): Promise<string | null> {
  const now = Date.now();
  const existing = refreshShares.get(supabase);
  if (existing && now - existing.at < REFRESH_SHARE_MS) return existing.p;
  const p = supabase.auth
    .refreshSession()
    .then(({ data }) => data?.session?.access_token ?? null)
    .catch(() => null);
  refreshShares.set(supabase, { at: now, p });
  return p;
}

export async function authFetch(
  supabase: SupabaseClient | null,
  url: string,
  init: AuthFetchInit = {},
): Promise<Response> {
  const { surface, expectedUid, headers: providedHeaders, ...restInit } = init;

  let { token, uid } = await getSessionInfo(supabase);

  // If no token, try refreshing the session first (shared, never throws)
  if (!token && supabase) {
    token = await sharedRefreshToken(supabase);
    // B234 — a refresh can restore a session `getSession()` hadn't hydrated
    // yet (the cold-boot race the guard above already handles); re-read uid
    // so the check below reflects who actually answered.
    if (token) ({ uid } = await getSessionInfo(supabase));
  }

  // B234 — refuse to send under a DIFFERENT account than the one this
  // action recorded when it started. `expectedUid` undefined = no guard: no
  // pre-existing caller passes it, so this is byte-identical to before.
  if (expectedUid !== undefined && uid !== expectedUid) {
    return stampAuthedUid(
      new Response(null, { status: 409, statusText: 'account changed mid-request' }),
      uid,
    );
  }

  const headers = buildHeaders(providedHeaders, token, surface);

  // Part B — API paths resolve against the shell's API base (no-op on web).
  const resolvedUrl = apiUrl(url);

  const response = await fetch(resolvedUrl, { ...restInit, headers });

  // 401 → attempt one refresh + retry (shared across concurrent callers)
  if (response.status === 401 && supabase) {
    // B234 — the same guard before the retry: a switch between the first
    // send and the 401 retry must not let the retry send under a new account.
    if (expectedUid !== undefined) {
      const { uid: uidNow } = await getSessionInfo(supabase);
      if (uidNow !== expectedUid) return stampAuthedUid(response, uidNow);
    }
    const newToken = await sharedRefreshToken(supabase);
    if (newToken) {
      const retryHeaders = buildHeaders(providedHeaders, newToken, surface);
      return stampAuthedUid(
        await fetch(resolvedUrl, { ...restInit, headers: retryHeaders }),
        uid,
      );
    }
  }

  return stampAuthedUid(response, uid);
}

// ─── B234 — an identity readback for callers with no SupabaseClient ───────
// A caller that only holds a bound `fetchAuthed` closure (no `supabase` of
// its own — the HealthKit import POST loop) cannot ask "who am I about to
// send as" before the fact. It CAN read who answered a call already made,
// via `authedUid`, and pass that back in as `expectedUid` on the NEXT call so
// authFetch's own (supabase-backed) check refuses to send if the account
// changed in between. A non-enumerable own property: `res.json()`/`res.ok`/
// spread all behave exactly as before.
const AUTHED_UID = Symbol('authed-uid');

function stampAuthedUid(response: Response, uid: string | null): Response {
  Object.defineProperty(response, AUTHED_UID, { value: uid, configurable: true });
  return response;
}

/** The uid whose session answered `response` (null = unauthenticated/unknown). */
export function authedUid(response: Response): string | null {
  return (response as unknown as Record<symbol, string | null>)[AUTHED_UID] ?? null;
}

/**
 * Read the current session's access token AND uid without forcing a
 * refresh, in ONE `getSession()` call (B234 — a second, separate call here
 * would double any auto-refresh `getSession()` triggers near expiry, the
 * exact stampede the refresh-share guard above exists to avoid). Both come
 * back null if there is no session OR the SupabaseClient hasn't
 * initialized yet.
 */
async function getSessionInfo(
  supabase: SupabaseClient | null,
): Promise<{ token: string | null; uid: string | null }> {
  if (!supabase) return { token: null, uid: null };
  try {
    const { data } = await supabase.auth.getSession();
    return {
      token: data?.session?.access_token ?? null,
      uid: data?.session?.user?.id ?? null,
    };
  } catch {
    return { token: null, uid: null };
  }
}

/**
 * Build the request headers — merges provided headers, applies
 * Content-Type default for JSON-bodied requests, attaches Bearer
 * token, and sets the X-Surface telemetry header when requested.
 *
 * Header normalization is conservative: caller-provided keys win
 * over our defaults, but we always attach the Bearer token if we
 * have one (even if caller passed an existing Authorization).
 */
function buildHeaders(
  provided: HeadersInit | undefined,
  token: string | null,
  surface: 'vanilla' | 'react' | undefined,
): Record<string, string> {
  const merged: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (provided) {
    // Normalize HeadersInit (Headers | string[][] | Record) to Record
    if (provided instanceof Headers) {
      provided.forEach((value, key) => {
        merged[key] = value;
      });
    } else if (Array.isArray(provided)) {
      for (const [k, v] of provided) merged[k] = v;
    } else {
      Object.assign(merged, provided);
    }
  }
  if (token) {
    merged['Authorization'] = `Bearer ${token}`;
  }
  if (surface) {
    merged['X-Surface'] = surface;
  }
  return merged;
}
